import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import IconWithTooltip from '../common/IconWithTooltip';
import OtoSpinner from '../common/OtoSpinner';
import { currency } from 'globals/currency';
import { fixDecimal } from 'utils/general';
import { calculateSmsParts } from 'utils/marketing';
import type {
  TCommonPropsForSpecificModals,
  TSendSmsModalProps,
} from 'types';

type SendSmsModalProps = TSendSmsModalProps & TCommonPropsForSpecificModals<string>;

const SendSmsModal: React.FC<SendSmsModalProps> = (props) => {
  const [smsMessage, setSmsMessage] = useState(props.smsText || '');
  const [loading, setLoading] = useState(false);
  const { customers: selectedCustomers, closeModal, onSave } = props;
  const smsCostPerPart = 0.1;
  const smsParts = calculateSmsParts(smsMessage);
  const sendMessage = () => {
    setLoading(true);
    onSave(smsMessage).finally(() => setLoading(false));
  };
  return (
    <Modal isOpen>
      <ModalHeader toggle={closeModal}>
        {i18next.t('send-sms-modal.header', { count: selectedCustomers.size })}
      </ModalHeader>
      <ModalBody>
        <label htmlFor="sms-message">{i18next.t('send-sms-modal.message-label')}</label>
        <Input
          type="textarea"
          placeholder={i18next.t('send-sms-modal.message-placeholder')}
          name="sms-message"
          value={smsMessage}
          onChange={(e) => setSmsMessage(e.target.value)}
        />
        <hr />
        <div className="mt-3">
          <div>
            {i18next.t('send-sms-modal.sms-cost', { cost: smsCostPerPart.toFixed(2), currency })}{' '}
            <IconWithTooltip
              id="sms-cost"
              text={i18next.t('send-sms-modal.sms-cost-tooltip')}
            />
          </div>
          <div>
            {i18next.t('send-sms-modal.sms-parts', { parts: smsParts, chars: smsMessage.length })}
          </div>
          <div>{i18next.t('send-sms-modal.sms-messages-count', { count: selectedCustomers.size })}</div>
          <hr />
          <div>
            {i18next.t('send-sms-modal.total-cost')}: {smsCostPerPart} * {smsParts} *{' '}
            {selectedCustomers.size} ={' '}
            {fixDecimal(smsParts * selectedCustomers.size * smsCostPerPart)}{' '}
            {currency}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {loading ? (
          <OtoSpinner />
        ) : (
          <>
            <Button type="button" color="secondary" onClick={closeModal}>
              {i18next.t('send-sms-modal.close')}
            </Button>
            <Button
              type="button"
              color="primary"
              onClick={sendMessage}
              disabled={!smsMessage}
            >
              {i18next.t('send-sms-modal.send')}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

SendSmsModal.propTypes = {
  customers: PropTypes.instanceOf(Set).isRequired,
  smsText: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default SendSmsModal;
